import { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarCheck,
  Target,
  Sparkles,
  CalendarDays,
  ListChecks,
  Vote,
  Compass,
  FolderKanban,
  BarChart3,
  Lightbulb,
  Image as ImageIcon,
  Brain,
  UserCircle2,
  Send,
  TelescopeIcon,
  Users2,
  MessageSquareCode,
  Webhook,
  Plug2,
  ScrollText,
  Building2,
  Shield,
  CreditCard,
  Settings,
  FileText,
  MoreHorizontal,
  Bug,
  Sun,
  Moon,
  LogOut,
  Server,
  Boxes,
  Mail,
  Megaphone,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type RailItem = {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
};

// Tenant rail (executive / EA / tenant admin). Order is intentional —
// daily flow (Home / Plan / Focus / Reflections), then Work, then
// Intelligence. Anything past `primary` lives in the More popover so the
// rail itself stays at ~10 visible icons + actions.
const PRIMARY_TENANT: RailItem[] = [
  { label: 'Home', path: '/', icon: LayoutDashboard },
  { label: 'Plan', path: '/plan', icon: CalendarCheck },
  { label: 'Focus Time', path: '/focus', icon: Target },
  { label: 'Reflections', path: '/reflections', icon: Sparkles },
  { label: 'Calendar', path: '/calendar', icon: CalendarDays },
  { label: 'Work Items', path: '/work-items', icon: ListChecks },
  { label: 'Decisions', path: '/decisions', icon: Vote },
  { label: 'Strategic Drivers', path: '/strategy', icon: Compass },
  { label: 'Projects', path: '/projects', icon: FolderKanban },
  { label: 'Reports', path: '/reports', icon: BarChart3 },
];

const OVERFLOW_TENANT: RailItem[] = [
  { label: 'Transcripts', path: '/transcripts', icon: FileText },
  { label: 'Ideas', path: '/ideas', icon: Lightbulb },
  { label: 'Image Capture', path: '/images', icon: ImageIcon },
  { label: 'Context Engine', path: '/context', icon: Brain },
  { label: 'Principal Profile', path: '/principal-profile', icon: UserCircle2 },
  { label: 'Delegation', path: '/delegation', icon: Send },
  { label: 'Horizon', path: '/horizon', icon: TelescopeIcon },
  { label: 'EA Workspace', path: '/ea-workspace', icon: Users2 },
  { label: 'Feature Requests', path: '/feature-requests', icon: Megaphone },
  { label: 'Prompt Management', path: '/prompt-management', icon: MessageSquareCode },
  { label: 'Webhooks', path: '/webhooks', icon: Webhook },
  { label: 'Connections', path: '/connections', icon: Plug2 },
  { label: 'Audit Log', path: '/audit-log', icon: ScrollText },
  { label: 'Organisation', path: '/organisation', icon: Building2 },
  { label: 'Admin', path: '/admin', icon: Shield },
  { label: 'Billing', path: '/billing', icon: CreditCard },
];

// Platform admin rail — different IA, lives at /platform/*
const PRIMARY_PLATFORM: RailItem[] = [
  { label: 'Overview', path: '/platform', icon: LayoutDashboard },
  { label: 'Tenants', path: '/platform/tenants', icon: Building2 },
  { label: 'Billing Centre', path: '/platform/billing', icon: CreditCard },
  { label: 'Products & Pricing', path: '/platform/products', icon: Boxes },
  { label: 'Email Settings', path: '/platform/email', icon: Mail },
  { label: 'Security & Diagnostics', path: '/platform/security', icon: Shield },
  { label: 'Bug Reports', path: '/platform/bug-reports', icon: Bug },
  { label: 'System', path: '/platform/system', icon: Server },
];

const OVERFLOW_PLATFORM: RailItem[] = [];

interface SlimRailProps {
  isPlatformAdmin: boolean;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onReportBug: () => void;
  onLogout: () => void;
  userInitial: string;
  userDisplayName: string;
  userRole: string;
  // Filter primary items by feature visibility (matches the existing AppLayout
  // rules — hide Transcripts when feature off, etc.). Caller passes a
  // predicate so we don't replicate the visibility table here.
  isVisible: (path: string) => boolean;
}

/**
 * 72px-wide collapsed icon rail that replaces the wide MUI drawer on desktop.
 *
 * Design:
 *   - Brand mark at top
 *   - Primary nav (~10 icons) — outlined lucide icons, hover tooltip
 *     with the route label, active state = subtle pill background +
 *     accent left bar
 *   - "More" popover at the bottom of the nav block — opens a 280px
 *     panel with the overflow routes labelled in full
 *   - Pinned bottom: Theme toggle, Bug report, Avatar (logout menu)
 *
 * Behavioural notes:
 *   - Uses NavLink so the active state is route-driven, not state-driven.
 *   - All hover surfaces tooltip the label, so the rail is keyboard- and
 *     mouse-accessible without label fatigue.
 */
export function SlimRail(props: SlimRailProps) {
  const {
    isPlatformAdmin,
    themeMode,
    onToggleTheme,
    onReportBug,
    onLogout,
    userInitial,
    userDisplayName,
    userRole,
    isVisible,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const primary = useMemo(
    () =>
      (isPlatformAdmin ? PRIMARY_PLATFORM : PRIMARY_TENANT).filter(item =>
        isVisible(item.path),
      ),
    [isPlatformAdmin, isVisible],
  );
  const overflow = useMemo(
    () =>
      (isPlatformAdmin ? OVERFLOW_PLATFORM : OVERFLOW_TENANT).filter(item =>
        isVisible(item.path),
      ),
    [isPlatformAdmin, isVisible],
  );

  const ThemeIcon = themeMode === 'dark' ? Sun : Moon;
  const themeTooltip = themeMode === 'dark' ? 'Switch to light' : 'Switch to dark';

  return (
    <TooltipProvider delayDuration={150}>
      <aside
        aria-label="Primary navigation"
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden w-[72px] flex-col items-center md:flex',
          // macOS-style sidebar glass: stronger blur + saturate, tinted
          // surface that lets the gradient backdrop bleed through, and a
          // hairline border that sits as a 1px gradient line on the right
          // edge instead of a hard divider.
          'border-r border-white/40 dark:border-white/10',
          'bg-white/45 dark:bg-white/5',
          'backdrop-blur-2xl backdrop-saturate-150',
          'shadow-[1px_0_2px_rgba(15,23,42,0.04),12px_0_40px_-20px_rgba(15,23,42,0.12)]',
          'dark:shadow-[1px_0_2px_rgba(0,0,0,0.4),12px_0_40px_-20px_rgba(0,0,0,0.5)]',
        )}
      >
        {/* Brand mark */}
        <div className="flex h-16 w-full items-center justify-center">
          <button
            type="button"
            onClick={() => navigate(isPlatformAdmin ? '/platform' : '/')}
            className={cn(
              'grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary',
              'transition-colors hover:bg-primary/20',
              isPlatformAdmin && 'bg-destructive/10 text-destructive hover:bg-destructive/20',
            )}
            aria-label="Cadence home"
          >
            <span className="text-base font-bold leading-none">S</span>
          </button>
        </div>

        {/* Primary nav */}
        <nav className="flex flex-1 flex-col items-center gap-1 px-2 py-2">
          {primary.map(item => (
            <RailIcon
              key={item.path}
              label={item.label}
              icon={item.icon}
              path={item.path}
              isActive={
                item.path === '/' || item.path === '/platform'
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path)
              }
            />
          ))}

          {overflow.length > 0 && (
            <Popover open={moreOpen} onOpenChange={setMoreOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="More"
                      className={cn(
                        'mt-2 grid h-10 w-10 place-items-center rounded-xl text-muted-foreground',
                        'transition-colors hover:bg-accent hover:text-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        moreOpen && 'bg-accent text-foreground',
                      )}
                    >
                      <MoreHorizontal className="h-5 w-5" />
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">More</TooltipContent>
              </Tooltip>
              <PopoverContent side="right" align="start" className="w-64">
                <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  More
                </p>
                <ul className="flex flex-col gap-0.5">
                  {overflow.map(item => {
                    const Icon = item.icon;
                    const active = location.pathname.startsWith(item.path);
                    return (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          onClick={() => setMoreOpen(false)}
                          className={cn(
                            'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
                            'hover:bg-accent hover:text-foreground',
                            active
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground/80',
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </PopoverContent>
            </Popover>
          )}
        </nav>

        {/* Pinned bottom — theme, bug report, avatar */}
        <div className="flex w-full flex-col items-center gap-1 border-t border-border/60 px-2 py-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onToggleTheme}
                aria-label={themeTooltip}
                className="grid h-10 w-10 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ThemeIcon className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{themeTooltip}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onReportBug}
                aria-label="Report a problem"
                className="grid h-10 w-10 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Bug className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Report a problem</TooltipContent>
          </Tooltip>

          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Account: ${userDisplayName}`}
                    className="mt-1 grid h-10 w-10 place-items-center rounded-full bg-primary/15 text-primary transition-colors hover:bg-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="text-sm font-semibold leading-none">
                      {userInitial}
                    </span>
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">{userDisplayName}</TooltipContent>
            </Tooltip>
            <PopoverContent side="right" align="end" className="w-60">
              <div className="px-2 pb-2 pt-1">
                <p className="text-sm font-semibold leading-tight">{userDisplayName}</p>
                <p className="text-xs text-muted-foreground">{userRole}</p>
              </div>
              <div className="my-1 border-t border-border/60" />
              <button
                type="button"
                onClick={() => navigate('/preferences')}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Settings className="h-4 w-4" />
                Preferences
              </button>
              <button
                type="button"
                onClick={onLogout}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </aside>
    </TooltipProvider>
  );
}

interface RailIconProps {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  isActive: boolean;
}

function RailIcon({ label, icon: Icon, path, isActive }: RailIconProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={path}
          aria-label={label}
          className={cn(
            'relative grid h-10 w-10 place-items-center rounded-xl transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isActive
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {isActive && (
            <span
              aria-hidden
              className="absolute -left-2 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-primary"
            />
          )}
          <Icon className="h-5 w-5" />
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
